import type { FilterOption, FilterConfig } from './dto.inventory';
import type { ParsedFilterOptions } from './transformers.inventory';

/*
|--------------------------------------------------------------------------
| STATIC FILTER OPTIONS
| These options are not fetched from the API and remain constant
|--------------------------------------------------------------------------
*/

export const CONDITION_OPTIONS: FilterOption[] = [
  { label: 'New', value: 'new' },
  { label: 'Pre-owned', value: 'pre-owned' },
];

export const SEARCH_WITHIN_OPTIONS: FilterOption[] = [
  { label: 'Any miles', value: '10000' },
  { label: '500 miles', value: '500' },
  { label: '250 miles', value: '250' },
  { label: '100 miles', value: '100' },
  { label: '50 miles', value: '50' },
];

export const AVAILABILITY_OPTIONS: FilterOption[] = [
  { label: 'Available Now', value: 'AVAILABLE_NOW' },
  { label: 'Est. Delivery 2 weeks', value: 'IN_TRANSIT' },
];

export const AIR_MODEL_YEAR_OPTIONS: FilterOption[] = [
  { label: '2026', value: '2026' },
  { label: '2025', value: '2025' },
  { label: '2023', value: '2023' },
];

export const GRAVITY_MODEL_YEAR_OPTIONS: FilterOption[] = [{ label: '2026', value: '2026' }];

export const AIR_TRIM_OPTIONS: FilterOption[] = [
  { label: 'Pure', value: 'PURE' },
  { label: 'Touring', value: 'AT' },
  { label: 'Grand Touring', value: 'GT' },
];

export const GRAVITY_TRIM_OPTIONS: FilterOption[] = [
  { label: 'Touring', value: 'AT' },
  { label: 'Grand Touring', value: 'GT' },
];

/*
|--------------------------------------------------------------------------
| HELPER FUNCTIONS
|--------------------------------------------------------------------------
*/

/**
 * Get trim options based on selected model, using prCodes for translated labels
 * @param isGravity - Whether Gravity model is selected
 * @param prCodes - Optional prCodes data for translated labels
 * @returns Appropriate trim options for the model
 */
export const getTrimOptionsForModel = (
  isGravity: boolean,
  prCodes?: ParsedFilterOptions
): FilterOption[] => {
  const validTrims = isGravity ? ['AT', 'GT'] : ['PURE', 'AT', 'GT'];
  const fallbackOptions = isGravity ? GRAVITY_TRIM_OPTIONS : AIR_TRIM_OPTIONS;

  if (!prCodes?.modelVariant?.length) {
    return fallbackOptions;
  }

  // Use prCodes labels for matching trims, maintaining order
  return validTrims
    .map((trimValue) => {
      const prCodeOption = prCodes.modelVariant.find((opt) => opt.value === trimValue);
      const fallback = fallbackOptions.find((opt) => opt.value === trimValue);
      return prCodeOption || fallback;
    })
    .filter((opt): opt is FilterOption => opt !== undefined);
};

/**
 * Get default trim values based on selected model
 * @param isGravity - Whether Gravity model is selected
 * @returns Default selected trim values for the model
 */
export const getDefaultTrimsForModel = (isGravity: boolean): string[] =>
  isGravity ? ['AT', 'GT'] : ['PURE', 'AT', 'GT'];

/**
 * Build dynamic filter config by combining static options with prCodes data
 * @param prCodes - Parsed prCodes data from API
 * @param isGravity - Whether Gravity model is selected
 * @returns Complete filter configuration
 */
export const buildFilterConfig = (
  prCodes: ParsedFilterOptions | undefined,
  isGravity: boolean
): FilterConfig => ({
  condition: CONDITION_OPTIONS,
  trim: getTrimOptionsForModel(isGravity, prCodes),
  searchWithin: SEARCH_WITHIN_OPTIONS,
  availability: AVAILABILITY_OPTIONS,
  exteriorColor: prCodes?.exteriorColor || [],
  interiorTheme: prCodes?.interiorTheme || [],
  wheels: prCodes?.wheels || [],
  appearance: prCodes?.appearance || [],
  driverAssistance: prCodes?.driverAssistance || [],
  soundSystem: prCodes?.soundSystem || [],
  seating: prCodes?.seating || [],
  packages: prCodes?.packages || [],
});
